import React, { useEffect, useRef, useState } from 'react';
import { DialogRequest, resolveDialog, subscribeDialogs } from '../utils/dialog';

/** Renders the oldest pending in-app dialog (see utils/dialog). Mounted once at the app root. */
const AppDialogHost: React.FC = () => {
  const [queue, setQueue] = useState<DialogRequest[]>([]);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const okRef = useRef<HTMLButtonElement>(null);
  const current = queue[0];

  useEffect(() => subscribeDialogs(setQueue), []);

  useEffect(() => {
    if (!current) return;
    setValue(current.defaultValue || '');
    const t = window.setTimeout(() => (current.kind === 'prompt' ? inputRef.current?.focus() : okRef.current?.focus()), 30);
    return () => window.clearTimeout(t);
  }, [current]);

  if (!current) return null;

  const cancel = () => resolveDialog(current.id, current.kind === 'confirm' ? false : null);
  const ok = () => resolveDialog(current.id, current.kind === 'prompt' ? value : true);
  const title = current.title || (current.kind === 'confirm' ? (current.danger ? 'Please confirm' : 'Confirm') : current.kind === 'prompt' ? 'Belivah' : '');
  const okLabel = current.confirmLabel || (current.kind === 'alert' ? 'OK' : current.danger ? (/^\s*(admin:\s*)?(delete|permanently)/i.test(current.message) ? 'Delete' : 'Yes, continue') : current.kind === 'prompt' ? 'Submit' : 'Yes');

  return (
    <div
      role="presentation"
      style={styles.backdrop}
      onMouseDown={(e) => { if (e.target === e.currentTarget && current.kind !== 'alert') cancel(); }}
    >
      <div
        role={current.kind === 'alert' ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-label={title || 'Message'}
        style={styles.card}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); if (current.kind === 'alert') ok(); else cancel(); }
        }}
      >
        {title && <h3 style={styles.title}>{title}</h3>}
        <p style={styles.message}>{current.message}</p>
        {current.kind === 'prompt' && (
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ok(); } }}
            style={styles.input}
          />
        )}
        <div style={styles.actions}>
          {current.kind !== 'alert' && (
            <button type="button" onClick={cancel} style={styles.cancel}>{current.cancelLabel || 'Cancel'}</button>
          )}
          <button ref={okRef} type="button" onClick={ok} style={{ ...styles.ok, ...(current.danger ? styles.danger : null) }}>
            {okLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  },
  card: {
    width: '100%', maxWidth: 400, background: 'var(--color-surface, #fff)', color: 'var(--color-text-primary, #111827)',
    borderRadius: 16, boxShadow: '0 20px 50px rgba(0,0,0,0.25)', padding: '20px 20px 16px', boxSizing: 'border-box',
  },
  title: { margin: '0 0 8px', fontSize: 17, fontWeight: 700 },
  message: { margin: 0, fontSize: 15, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: 'var(--color-text-secondary, #374151)' },
  input: {
    width: '100%', boxSizing: 'border-box', marginTop: 12, padding: '10px 12px', fontSize: 15, borderRadius: 10,
    border: '1px solid var(--color-border, #d1d5db)', background: 'var(--color-surface, #fff)', color: 'inherit',
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18, flexWrap: 'wrap' },
  cancel: {
    padding: '9px 16px', borderRadius: 10, border: '1px solid var(--color-border, #d1d5db)', background: 'transparent',
    color: 'inherit', fontWeight: 600, fontSize: 14, cursor: 'pointer',
  },
  ok: { padding: '9px 18px', borderRadius: 10, border: 'none', background: '#0B6B6F', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  danger: { background: '#dc2626' },
};

export default AppDialogHost;
